import { AddPetsToClinicRequest, ClinicModel, IClinicModel, IOpenClinicRequest, IUpdateClinicUserRoleRequest } from "@common/models";
import { IClinicCancelRequest } from "@common/models/requests/ClinicCancelRequest";
import { IRebookReservationResponse, RebookReservationResponse } from "@common/models/responses";
import { ServiceHelper } from "@common/services/serviceHelper";
import { http } from "@common/utils";
import { trackPromise } from "react-promise-tracker";

export class ClinicService {
    private static baseUrl: string = '/api/clinic';
    private static publicUserHeader: any = {
        'vpca': '351BD313-2C19-442B-BC87-C69356EB59EF',
        'Content-Type': 'application/json',
        'Cache-Control': 'no-cache'
    };

    private static isDIClinic: boolean = false;

    public static tags = {
        getReservationForRebooking: "getReservationForRebooking",
        getClinicIdByHospitalId: "getClinicIdByHospitalId",
        getClinicById: "getClinicById",
        openClinic: "openClinic",
        joinClinic: "joinClinic",
        viewClinic: "viewClinic",
        cancelClinic: "cancelClinic",
        addPetsToClinic: "addPetsToClinic",
        logOut: "logOut",
        updateClinicUserRole: "updateClinicUseRole",
    }

    static getReservationForRebooking = (clinicId: number, reservationpublicId: string): Promise<RebookReservationResponse> =>
        trackPromise(new Promise<RebookReservationResponse>((resolve, reject) => {
            http(`${this.baseUrl}/reservation/rebook/${clinicId}/${reservationpublicId}`)
                .mergeOptions({ headers: this.publicUserHeader })
                .get<IRebookReservationResponse>()
                .then(json => resolve(new RebookReservationResponse(json)))
                .catch(reject)
        }), this.tags.getReservationForRebooking);

    static getClinicIdByHospitalId = (hospitalId: number,): Promise<number> =>
        trackPromise(new Promise<number>((resolve, reject) => {
            http(`${this.baseUrl}/hospital/${hospitalId}`)
                .mergeOptions({ headers: this.publicUserHeader })
                .get<number>()
                .then(json => resolve(json))
                .catch(reject)
        }), this.tags.getClinicIdByHospitalId);

    static getClinicById = (clinicId: number): Promise<ClinicModel> => {
        return trackPromise(new Promise<ClinicModel>((resolve, reject) => {
            http(`${this.baseUrl}/${clinicId}`).get<IClinicModel>()
                .then((response) => {
                    resolve(new ClinicModel(response));
                })
                .catch((err) => {
                    reject(err);
                })
        }), this.tags.getClinicById)
    }

    static openClinic = (clinicRequest: IOpenClinicRequest): Promise<ClinicModel> => {
        return trackPromise(new Promise<ClinicModel>((resolve, reject) => {
            http(`${this.baseUrl}/open/${clinicRequest.clinicId}`)
                .post<IClinicModel>(clinicRequest)
                .then(json => new ClinicModel(json))
                .then(resolve)
                .catch((err) => {
                    ServiceHelper.showError("Failed to open clinic.");
                    reject(err);
                })
        }), this.tags.openClinic);
    }

    static joinClinic = (clinicRequest: IOpenClinicRequest): Promise<ClinicModel> => {
        return trackPromise(new Promise<ClinicModel>((resolve, reject) => {
            http(`${this.baseUrl}/join/${clinicRequest.clinicId}/${this.isDIClinic}/${clinicRequest.clinicRoleId}`)
                .get<IClinicModel>()
                .then(json => new ClinicModel(json))
                .then(resolve)
                .catch((err) => {
                    ServiceHelper.showError("Failed to join clinic.");
                    reject(err);
                })
        }), this.tags.joinClinic);
    }

    static viewClinic = (clinicRequest: IOpenClinicRequest): Promise<ClinicModel> => {
        return trackPromise(new Promise<ClinicModel>((resolve, reject) => {
            http(`${this.baseUrl}/view/${clinicRequest.clinicId}`)
                .get<IClinicModel>()
                .then(json => new ClinicModel(json))
                .then(resolve)
                .catch((err) => {
                    ServiceHelper.showError("Failed to join clinic.");
                    reject(err);
                })
        }), this.tags.viewClinic);
    }

    static cancelClinic = (cancelClinicRequest: IClinicCancelRequest): Promise<ClinicModel> => {
        return trackPromise(new Promise((resolve, reject) => {
            http(`${this.baseUrl}/cancel`)
                .post<IClinicCancelRequest>(cancelClinicRequest)
                .then((json => new ClinicModel(json)))
                .then(resolve)
                .catch((err) => {
                    reject(err)
                })
        }), this.tags.cancelClinic)
    }

    static addPetsToClinic = (request: AddPetsToClinicRequest): Promise<ClinicModel> => {
        return trackPromise(new Promise((resolve, reject) => {
            http(`${this.baseUrl}/addpets/${request.clinicId}`)
                .post<AddPetsToClinicRequest>(request)
                .then(json => new ClinicModel(json))
                .then(resolve)
                .catch(err => {
                    reject(err);
                });
        }), this.tags.addPetsToClinic)
    }

    static updateClinicUserRole = (request: IUpdateClinicUserRoleRequest): Promise<ClinicModel> => {
        return trackPromise(new Promise((resolve, reject) => {
            http(`${this.baseUrl}/updateClinicUserRole`)
                .post<IUpdateClinicUserRoleRequest>(request)
                .then(json => new ClinicModel(json))
                .then(resolve)
                .catch(err => reject(err))
        }), this.tags.updateClinicUserRole)
    }
}
